import { Badge, Button, Drawer, EmptyState, IconButton, Skeleton, toast } from "@cafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Bell, BellOff, Settings } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { formatDateTime } from "@/shared/lib/options";
import { useRealtime } from "@/shared/realtime/socket";
import { notificationKeys, notificationsApi, useNotifications, type NotificationDto } from "./api";

/** Раздел ERP из уведомления (`platform.update` — новая функция): только внутренний путь */
const pathOf = (n: Pick<NotificationDto, "data">) => {
  const p = (n.data as { path?: unknown } | null)?.path;
  return typeof p === "string" && p.startsWith("/") && !p.startsWith("//") ? p : null;
};

/** Колокольчик в шапке: число непрочитанных, список последних уведомлений (ТЗ 16 §16.1, in-app канал). */
export function NotificationBell() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const q = useNotifications();
  const unread = q.data?.meta.unread ?? 0;
  useRealtime<{ type: string; title: string }>("notification.created", (e) => {
    void qc.invalidateQueries({ queryKey: notificationKeys.list });
    // Дневной отчёт — без всплывашки; вызов к столу показывает свой тост (table.call_waiter/request_bill); остальное — короткий тост
    if (e.data.type === "stock.low") toast.warning(e.data.title, { duration: 10_000 });
    else if (e.data.type === "platform.update") toast.info(`${t("notifications.whatsNew")}: ${e.data.title}`, { duration: 15_000, action: { label: t("notifications.open"), onClick: () => setOpen(true) } });
    else if (e.data.type === "platform.message") toast.info(`${t("notifications.fromCoca")}: ${e.data.title}`, { duration: 15_000, action: { label: t("notifications.open"), onClick: () => setOpen(true) } });
    else if (e.data.type !== "report.daily" && e.data.type !== "table.call") toast.info(e.data.title, { duration: 8000 });
  });
  const readAll = async () => {
    await notificationsApi.readAll().catch(() => undefined);
    void qc.invalidateQueries({ queryKey: notificationKeys.list });
  };
  const close = () => {
    setOpen(false);
    if (unread) void readAll();
  };
  const go = (path: string) => { close(); navigate(path); };
  return (
    <>
      <span className="relative">
        <IconButton variant="ghost" size="sm" aria-label={unread ? t("notifications.bellUnread", { n: unread }) : t("notifications.bell")} icon={<Bell />} onClick={() => setOpen(true)} />
        {unread > 0 && (
          <span aria-hidden className="pointer-events-none absolute -top-0.5 -right-0.5 grid min-w-5 place-items-center rounded-full bg-danger px-1 text-[11px] leading-5 font-semibold text-white tabular-nums">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </span>
      {open && (
        <Drawer open onOpenChange={(o) => !o && close()} width="sm" title={t("notifications.title")} flush
          footer={<>
            <Button variant="outline" leftIcon={<Settings />} onClick={() => { close(); navigate("/settings/notifications"); }}>{t("notifications.settings")}</Button>
            {unread > 0 && <Button variant="ghost" onClick={() => void readAll()}>{t("notifications.readAll")}</Button>}
          </>}>
          {!q.data ? <div className="p-4"><Skeleton className="h-40" /></div> : q.data.items.length === 0 ? (
            <EmptyState className="m-4" icon={<BellOff />} title={t("notifications.empty")} description={t("notifications.emptyHint")} />
          ) : (
            <ul className="divide-y divide-border">
              {q.data.items.map((n) => (
                <li key={n.id} className="flex gap-3 px-5 py-4">
                  <span aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${n.readAt ? "bg-transparent" : "bg-primary"}`} />
                  <div className="min-w-0 flex-1">
                    {n.type === "platform.message" && <Badge tone="primary" className="mb-1">{t("notifications.fromCoca")}</Badge>}
                    {n.type === "platform.update" && <Badge tone="success" className="mb-1">{t("notifications.whatsNew")}</Badge>}
                    <p className="font-semibold">{n.title}{!n.readAt && <span className="sr-only"> · {t("notifications.new")}</span>}</p>
                    <p className="mt-1 text-sm whitespace-pre-line text-muted">{n.body}</p>
                    {pathOf(n) && <Button variant="outline" size="sm" className="mt-2" rightIcon={<ArrowRight />} onClick={() => go(pathOf(n)!)}>{t("notifications.open")}</Button>}
                    <p className="mt-1 text-xs text-subtle">{formatDateTime(n.createdAt)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Drawer>
      )}
    </>
  );
}
